/**
 * AIMDv2 – AddFileModal
 *
 * Modal dialog for uploading a file document.
 * Shows a drop zone / file picker, the selected file name, and an
 * optional "Context or instructions for processing" field.
 */

import React, { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DosenConfirmModal from '../../../components/DosenConfirmModal';
import DosenInput from '../../../components/DosenInput';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';

interface AddFileModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (file: File, instructions: string) => void;
}

const AddFileModal: React.FC<AddFileModalProps> = ({ open, onClose, onSubmit }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [instructions, setInstructions] = useState('');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleSubmit = () => {
    if (!selectedFile) return;
    onSubmit(selectedFile, instructions.trim());
    setSelectedFile(null);
    setInstructions('');
    onClose();
  };

  const handleClose = () => {
    setSelectedFile(null);
    setInstructions('');
    onClose();
  };

  const fileLabel = selectedFile?.name.split('.').pop()?.toUpperCase() || '';

  return (
    <DosenConfirmModal open={open} onClose={handleClose} title="Add files to your Program">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', py: 1 }}>
        {/* File drop zone / selector */}
        <Box
          onClick={() => fileInputRef.current?.click()}
          sx={{
            py: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            cursor: 'pointer'
          }}
        >
          {selectedFile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <InsertDriveFileOutlinedIcon
                sx={{ fontSize: 24, color: 'var(--Stroke-icon-dim, #868686)' }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 500,
                      fontFamily: 'Inter',
                      color: 'var(--Text-text-primary, #FDFDFD)',
                      lineHeight: '16px'
                    }}
                  >
                    {selectedFile.name}
                  </Typography>
                  {fileLabel && (
                    <Box
                      sx={{
                        px: 1,
                        py: '5px',
                        background: 'var(--Fill-Button-button-gray, #565656)',
                        borderRadius: '20px'
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          fontFamily: 'Inter',
                          color: '#FDFDFD',
                          textTransform: 'uppercase',
                          lineHeight: '12px'
                        }}
                      >
                        {fileLabel}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                border: '2px dashed var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))',
                borderRadius: '8px',
                py: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <InsertDriveFileOutlinedIcon
                sx={{ fontSize: 32, color: 'var(--Stroke-icon-dim, #868686)' }}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  fontFamily: 'Inter',
                  color: 'var(--Text-text-secondary, #B6B6B6)'
                }}
              >
                Click to browse or drag and drop files here
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontFamily: 'Inter',
                  color: 'var(--Text-text-tertiary, #868686)'
                }}
              >
                Supported: PDF, DOCX, DOC, TXT, MD, RTF
              </Typography>
            </Box>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt,.md,.rtf"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
        </Box>

        <DosenInput
          label="Context or instructions for processing"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder='e.g. "Prioritize our proprietary framework in sections 2 to 4"'
        />

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <DosenButton
            tone="cta"
            sizeVariant="sm"
            onClick={handleSubmit}
            disabled={!selectedFile}
            sx={{ minWidth: 192 }}
          >
            Add File
          </DosenButton>
        </Box>
      </Box>
    </DosenConfirmModal>
  );
};

export default AddFileModal;
